import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../../../components/ui";
import googleIcon from "../../../assets/svg/auth/google.svg";
import { EXTERNAL_LINKS, ROUTES } from "../../../routes/paths";

/**
 * Collects the details and hands both routes to the consent screen. Nothing
 * here talks to Clerk: under the DPDP Act the account cannot be created before
 * consent is given, and consent is the next screen - so this one only decides
 * which way the user is going and carries the form along.
 */
const SignUpPage = () => {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  // Validation states
  const [nameError, setNameError] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  const validateName = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return "Full name is required";
    }
    if (trimmed.length < 3) {
      return "Full name must be at least 3 characters";
    }
    if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(trimmed)) {
      return "Full name can only contain letters, spaces, . ' and -";
    }
    return "";
  };

  const validateEmail = (value: string): string => {
    if (!value) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFullName(value);
    if (nameTouched) {
      setNameError(validateName(value));
    }
  };

  // Both blur handlers leave an empty field alone: it was never typed in, so
  // it is unused rather than wrong, and the Google button below would otherwise
  // flag the form on the way past it. Submitting still asks for both.
  const handleNameBlur = () => {
    if (!fullName) {
      setNameError("");
      return;
    }

    setNameTouched(true);
    setNameError(validateName(fullName));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    if (!email) {
      setEmailError("");
      return;
    }

    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleSubmit = () => {
    const nameErr = validateName(fullName);
    const emailErr = validateEmail(email);

    setNameError(nameErr);
    setNameTouched(true);
    setEmailError(emailErr);
    setEmailTouched(true);

    if (nameErr || emailErr) {
      return;
    }

    // The details ride along in router state; `signUp.create` runs on the
    // consent screen once the boxes are ticked.
    navigate(ROUTES.consent, {
      state: { method: "email", fullName: fullName.trim(), email },
    });
  };

  // The Google handshake is started from the consent screen too - going
  // straight to Google from here would have Clerk create the account before
  // the user has agreed to anything.
  const handleGoogle = () => {
    navigate(ROUTES.consent, { state: { method: "google" } });
  };

  const isFormValid = !validateName(fullName) && !validateEmail(email);

  // 16px on a phone, the design's 15px from sm up. iOS zooms the page into any
  // field it has to enlarge to read, and does not zoom back out when the field
  // is left - so a 15px input on a phone costs the user the rest of the screen.
  //
  // The phone field is taller and rounder than the designed one, white at
  // every width, and answers focus with a ring as well as a border - the same
  // field the log-in screen builds, and the reasoning is written out there.
  // Only the size, radius and ring are undone at `sm:`.
  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-4 border rounded-xl bg-white text-[16px] text-gray-900 placeholder-[#9AA4AE] focus:outline-none transition-all sm:px-4 sm:py-3.5 sm:rounded-lg sm:text-[15px] sm:focus:ring-0 ${
      hasError
        ? "bg-[#FEF6F6] border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/15"
        : "border-[#DCE0E4] focus:border-[#00C2CB] focus:ring-4 focus:ring-[#00C2CB]/15"
    }`;

  /** The label above a field on a phone, and nothing from sm up - where the
   *  design puts the placeholder alone in the box. Same as the log-in screen's:
   *  a placeholder is gone the moment a thumb starts typing, and on a phone the
   *  field is far enough from the sentence at the top that it has to keep
   *  saying what it is. */
  const labelClasses = "mb-2 block text-[13px] font-medium text-[#1E1F24] sm:hidden";

  return (
    <AuthLayout phoneFill>
      {/* Sized against the column it heads - the same reasoning as the log-in,
          OTP and consent screens' headings. */}
      <h1 className="text-[30px] leading-[1.15] font-semibold text-[#1F2937] sm:text-[24px] sm:leading-tight">
        Sign up
      </h1>
      <p className="mt-2 text-[15px] text-[#62636C] sm:mt-1 sm:text-[14px]">
        Please enter your details to create account
      </p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          <label htmlFor="signup-name" className={labelClasses}>
            Full name
          </label>
          <input
            id="signup-name"
            type="text"
            value={fullName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            autoComplete="name"
            // A name is capitalised per word and never autocorrected; the
            // action key moves on to the address rather than submitting.
            autoCapitalize="words"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            className={inputClasses(!!nameError && nameTouched)}
            placeholder="Full name"
          />
          {nameError && nameTouched && (
            <p className="mt-1.5 text-xs text-red-500">{nameError}</p>
          )}
        </div>

        <div>
          <label htmlFor="signup-email" className={labelClasses}>
            Email address
          </label>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            autoComplete="email"
            // As on the log-in screen: no capitalisation or autocorrect on an
            // address, and the action key finishes the form.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="go"
            className={inputClasses(!!emailError && emailTouched)}
            placeholder="yourname@email.com"
          />
          {emailError && emailTouched && (
            <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
          )}
        </div>

        {/* Clerk's bot check moved to the consent screen along with
            `signUp.create` - it has to be in the DOM where the call is made. */}

        {/* The lift and the disabled hairline, both for the reasons written out
            on the log-in screen's submit. */}
        <Button
          type="submit"
          variant="primary"
          size="touch"
          fullWidth
          disabled={!isFormValid}
          className="mt-2 enabled:shadow-[0_10px_24px_-12px_rgba(0,194,203,0.9)] disabled:border disabled:border-[#B7EDF3] sm:disabled:border-0 sm:shadow-none"
        >
          Create account
        </Button>

        {/* <p className="text-center text-[13px] leading-relaxed text-[#62636C]">
          By creating an account, you agree to our{" "}
          <a
            href={EXTERNAL_LINKS.privacyPolicy}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00C2CB] font-medium hover:underline"
          >
            Privacy Policy
          </a>
        </p> */}
      </form>

      <div className="my-6 sm:my-7 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#E3E7EA]" />
        <span className="text-sm font-medium text-[#9AA4AE]">OR</span>
        <span className="h-px flex-1 bg-[#E3E7EA]" />
      </div>

      <Button
        variant="secondary"
        size="touch"
        fullWidth
        onClick={handleGoogle}
        startIcon={<img src={googleIcon} alt="" className="w-5 h-5" />}
      >
        Continue with Google
      </Button>

      {/* Pins the last line to the foot of a phone screen, and collapses to
          nothing where the content already fills it - see the log-in screen,
          which explains why only this line is pinned and not the submit. How
          far off the edge it stops is the shell's bottom padding. */}
      <div className="flex-1 sm:hidden" aria-hidden />

      <p className="mt-8 sm:mt-12 text-center text-[14px] text-[#3A4652]">
        Already have an account?{" "}
        {/* Padded out to a thumb, and pulled back in by the same amount so the
            sentence keeps its spacing - see the log-in screen. */}
        <button
          type="button"
          onClick={() => navigate(ROUTES.login)}
          className="-m-2 p-2 text-[#00C2CB] font-medium hover:underline"
        >
          Log in
        </button>
      </p>
    </AuthLayout>
  );
};

export default SignUpPage;
